import React, { useState, useEffect, useRef } from "react";
import { ShoppingCart, Music, Play, Pause, ExternalLink, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "music", label: "Music" },
  { key: "merch", label: "Merchandise" },
  { key: "support", label: "Support" },
];

function MusicPreviewPlayer({ product }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  const previewUrl = product.preview_file_url || product.audio_file_url;
  const maxDuration = product.preview_duration_seconds || 20;

  const togglePlay = () => {
    if (!previewUrl) { toast.error("No preview available"); return; }
    if (playing) {
      audioRef.current?.pause();
      clearInterval(timerRef.current);
      setPlaying(false);
    } else {
      const audio = audioRef.current;
      audio.currentTime = 0;
      audio.play().catch(() => toast.error("Unable to play audio"));
      setPlaying(true);
      setProgress(0);
      timerRef.current = setInterval(() => {
        if (audio.currentTime >= maxDuration) {
          audio.pause();
          clearInterval(timerRef.current);
          setPlaying(false);
          setProgress(0);
        } else {
          setProgress((audio.currentTime / maxDuration) * 100);
        }
      }, 100);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    return () => { clearInterval(timerRef.current); audio?.pause(); };
  }, []);

  return (
    <div className="flex items-center gap-2 mt-2">
      <audio ref={audioRef} src={previewUrl} preload="none" />
      <button onClick={togglePlay} className="w-8 h-8 rounded-full bg-royal-purple text-white flex items-center justify-center hover:bg-royal-purple/80 transition" data-testid={`play-btn-${product.id}`}>
        {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
      </button>
      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-kingdom-gold rounded-full transition-all" style={{ width: `${progress}%` }} />
      </div>
      <span className="text-xs text-slate-400">{maxDuration}s preview</span>
    </div>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const { addItem } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch {
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filtered = activeCategory === "all"
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen" data-testid="shop-page">
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">Official Shop</span>
          <h1 className="heading-hero mt-2 mb-4">Kingdom Store</h1>
          <p className="body-large text-slate-300 max-w-2xl mx-auto">
            Music, merchandise, and more from Yunah'Shach'Qal Yashar'al.
          </p>
        </div>
      </section>

      {/* Etsy Banner */}
      <section className="bg-kingdom-gold py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>
            <p className="font-bold text-royal-blue">Shop Merchandise on Etsy</p>
            <p className="text-sm text-royal-blue/80">Physical items ship via AncientPathFactory</p>
          </div>
          <a href="https://www.etsy.com/shop/AncientPathFactory" target="_blank" rel="noopener noreferrer">
            <Button className="bg-royal-blue text-white hover:bg-royal-blue/90">
              <ExternalLink className="w-4 h-4 mr-2" />Visit Etsy Shop
            </Button>
          </a>
        </div>
      </section>

      <section className="bg-parchment py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {CATEGORIES.map(cat => (
              <button
                key={cat.key}
                className={`px-4 py-2 rounded text-sm font-semibold transition ${activeCategory === cat.key ? 'bg-royal-blue text-white' : 'bg-white text-royal-blue border border-royal-blue/20 hover:bg-royal-blue/5'}`}
                onClick={() => setActiveCategory(cat.key)}
                data-testid={`cat-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12"><p className="text-slate-500">Loading products...</p></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12"><Package className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No products found in this category.</p></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(product => (
                <Card key={product.id} className="card-official overflow-hidden" data-testid={`product-${product.id}`}>
                  {product.cover_image_url && (
                    <div className="h-48 bg-slate-100 overflow-hidden">
                      <img src={product.cover_image_url} alt={product.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        {product.product_type === "music" && <span className="text-xs text-royal-purple font-semibold uppercase">Music</span>}
                        {product.source === "etsy" && <span className="text-xs text-kingdom-gold font-semibold uppercase">Etsy</span>}
                        <h3 className="font-semibold text-royal-blue">{product.title || product.name}</h3>
                        {product.artist && <p className="text-xs text-slate-500">{product.artist}</p>}
                      </div>
                      <span className="font-bold text-lg text-royal-blue">${product.price?.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{product.description}</p>

                    {/* Music preview */}
                    {product.product_type === "music" && product.audio_file_url && (
                      <MusicPreviewPlayer product={product} />
                    )}

                    {/* Buy / Link */}
                    <div className="mt-3">
                      {product.source === "etsy" && product.external_url ? (
                        <a href={product.external_url} target="_blank" rel="noopener noreferrer" className="block">
                          <Button className="w-full btn-gold" data-testid={`buy-${product.id}`}>
                            <ExternalLink className="w-4 h-4 mr-2" />Buy on Etsy
                          </Button>
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="w-full btn-gold inline-flex items-center justify-center gap-2 py-2 rounded font-semibold"
                          onClick={() => addItem({
                            internal_id: product.id,
                            name: product.title || product.name,
                            price: Number(product.price || 0),
                            type: "one_time",
                            image: product.cover_image_url || null,
                          })}
                          data-testid={`buy-${product.id}`}
                        >
                          {product.product_type === "music" ? <Music className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                          Add to Cart ${product.price?.toFixed(2)}
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
